import React, { useMemo, useState } from 'react'
import { Tag } from 'antd'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Package, Zap, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { reportsApi } from '@/api/services'
import { useAuthStore } from '@/stores/authStore'
import { Approval } from '@/types'
import dayjs from 'dayjs'
import { leapTablePagination } from '@/components/table/defaultPagination'
import {
  buildOpenActionColumn,
  columnsScrollWidth,
  translateDashboardApprovalColumns,
} from '@/components/table/approvalTableColumns'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { approvalRowClass } from '@/components/table/statusRowClass'
import { isTableChromeTarget } from '@/components/table/tableChrome'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'
import {
  PageShell,
  PageHero,
  DashboardSection,
  DashboardPanel,
  PodStatusKpiGrid,
  WindowSegmentPicker,
  RefreshingRegion,
  PageLoadingSkeleton,
} from '@/components/dashboard'
import { cn } from '@/utils/cn'

const DASHBOARD_WINDOW_PRESETS = [
  { days: 7, labelKey: 'last7days', fallback: '7 days' },
  { days: 15, labelKey: 'last15days', fallback: '15 days' },
  { days: 30, labelKey: 'last30days', fallback: '30 days' },
  { days: 60, labelKey: 'last60days', fallback: '60 days' },
  { days: 90, labelKey: 'last90days', fallback: '90 days' },
] as const

const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [windowDays, setWindowDays] = useState<number>(30)
  const [isExporting, setIsExporting] = useState(false)
  const { t } = useTranslation(['Sidebar', 'Common', 'ReportsPOD', 'PendingPOD'])

  const { data: dashboardData, isPending, isFetching } = useQuery({
    queryKey: ['dashboard', windowDays],
    queryFn: () => reportsApi.getDashboard({ days: windowDays }),
    select: (response: any) => response.data?.data,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    const toastId = toast.loading('Preparing report export...')
    try {
      const blob = await reportsApi.exportToExcel()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `POD_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      toast.success('Report downloaded successfully', { id: toastId })
    } catch (error: any) {
      console.error('Export failed:', error)
      let msg = 'Failed to export report'
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const json = JSON.parse(text)
          msg = json.detail || json.message || json.error || msg
        } catch {
          // ignore parsing error
        }
      } else if (error?.message) {
        msg = error.message
      }
      toast.error(msg, { id: toastId })
    } finally {
      setIsExporting(false)
    }
  }

  const columns = useMemo(() => {
    const dataCols = translateDashboardApprovalColumns(t)
    const actionCol = buildOpenActionColumn({
      t,
      onOpen: record => navigate(`/approvals/${record.id}`),
    })
    return [...dataCols, actionCol]
  }, [t, navigate])

  const tableScrollX = useMemo(() => columnsScrollWidth(columns as any), [columns])

  const stats = dashboardData?.statistics
  const recent = dashboardData?.recentApprovals ?? []

  const metrics = useMemo(() => {
    const total = Number(stats?.total ?? 0)
    const pending = Number(stats?.pending ?? 0)
    const approved = Number(stats?.approved ?? 0)
    const rejected = Number(stats?.rejected ?? 0)
    return { total, pending, approved, rejected }
  }, [stats?.approved, stats?.pending, stats?.rejected, stats?.total])

  const windowPresets = useMemo(
    () =>
      DASHBOARD_WINDOW_PRESETS.map(({ days, labelKey, fallback }) => ({
        days,
        label: t(`Common:general.${labelKey}`, `Last ${fallback}`),
      })),
    [t],
  )

  const isInitialLoad = isPending && dashboardData == null
  const isRefreshing = isFetching && !isInitialLoad

  if (isInitialLoad) {
    return (
      <PageShell>
        <PageLoadingSkeleton showToolbar />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHero
        greetingName={user?.name}
        subtitle={t(
          'Common:general.kpiRollingDesc',
          'KPIs below use the rolling window you select; POD Reports stays all-time unless you filter dates there.',
        )}
        badges={[
          { label: 'Live', live: true },
          { label: 'POD Command', accent: true, icon: Zap },
          { label: 'Proof of delivery', icon: Package },
        ]}
        actions={
          <Button variant="outline" onClick={handleExport} disabled={metrics.total === 0}>
            {t('ReportsPOD:actions.excelReport', 'Export report')}
          </Button>
        }
      />

      <div className={cn('dashboard-toolbar', isRefreshing && 'dashboard-toolbar--refreshing')}>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t('Common:general.rollingWindow', 'Rolling window')}
          </span>
          <WindowSegmentPicker presets={windowPresets} value={windowDays} onChange={setWindowDays} />
        </div>
        {isRefreshing ? (
          <span className="dashboard-refresh-pill">
            <span className="dashboard-refresh-pill__dot" aria-hidden />
            {t('Common:general.refreshingWindow', 'Refreshing window')}
          </span>
        ) : null}
      </div>

      <RefreshingRegion active={isRefreshing}>
        <PodStatusKpiGrid
          metrics={metrics}
          refreshing={isRefreshing}
          labels={{
          total: t('Common:general.total', 'Total'),
          pending: t('ReportsPOD:status.Pending', 'Pending'),
          approved: t('ReportsPOD:status.Approved', 'Approved'),
          rejected: t('ReportsPOD:status.Rejected', 'Rejected'),
        }}
        onTotalClick={() => navigate('/PODReports')}
        onPendingClick={() => navigate('/pendingPOD')}
        onApprovedClick={() => navigate('/PODReports?status=Approved')}
        onRejectedClick={() => navigate('/PODReports?status=Rejected')}
        />
      </RefreshingRegion>

      <RefreshingRegion active={isRefreshing}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="min-w-0 lg:col-span-3">
          <DashboardSection
            title={t('Common:general.recentApprovals', 'Recent Approvals')}
            actions={
              <Button size="sm" onClick={handleExport} disabled={isExporting}>
                {isExporting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  t('ReportsPOD:actions.excelReport', 'Export report')
                )}
              </Button>
            }
          >
            <DashboardPanel flush className="leap-data-page-card">
              <LeapDataTable<Approval>
                dataSource={recent}
                columns={columns}
                rowKey="id"
                loading={false}
                scroll={{ x: tableScrollX }}
                rowClassName={record => approvalRowClass(record.status)}
                pagination={{ ...leapTablePagination(recent.length), pageSize: 5 }}
                onRow={record => ({
                  onClick: e => {
                    if (isTableChromeTarget(e.target)) return
                    navigate(`/approvals/${record.id}`)
                  },
                  style: { cursor: 'pointer' },
                })}
              />
            </DashboardPanel>
          </DashboardSection>
        </div>

        <div className="lg:col-span-1">
          <DashboardSection
            title={t('Common:general.documentTypes', 'Document Types')}
            subtitle={t('Common:general.breakdownWindow', 'Breakdown for the selected rolling window')}
          >
            <DashboardPanel>
              <div className="space-y-3">
                {Object.entries(stats?.byType || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between gap-2">
                    <Tag
                      color={
                        type === 'ALT' ? 'blue' : type === 'DHR' ? 'green' : type === 'GRN' ? 'orange' : 'purple'
                      }
                    >
                      {type}
                    </Tag>
                    <span className="text-sm font-bold tabular-nums">{String(count)}</span>
                  </div>
                ))}
                {!Object.keys(stats?.byType || {}).length ? (
                  <p className="text-sm text-muted-foreground">No data in this window.</p>
                ) : null}
              </div>
            </DashboardPanel>
          </DashboardSection>
        </div>
      </div>
      </RefreshingRegion>
    </PageShell>
  )
}

export default DashboardPage
